/**
 * Holly — the sheep, and the second-most-important character in the game.
 *
 * ## The one idea this file exists to protect
 *
 * `docs/act-2-plan.md`: *"Rosalie is round, low, warm coral and frantic. Holly is
 * tall, vertical, off-white, and calm. In every two-shot they compose as a
 * **circle and a column**. That contrast is the relationship, and it is legible
 * at 64 px before a single animation exists."*
 *
 * Everything below is that sentence turned into numbers. If a change makes her
 * rounder, lower, warmer or busier, it is wrong even if it looks better on its
 * own in the workbench — she is never seen on her own in the game.
 *
 * ## What a sheep fails as, and the three properties that stop it
 *
 * Otto's file records two failed attempts and their shared diagnosis. A sheep has
 * its own single failure mode and it is worth naming before anybody edits this:
 * **a cloud on four sticks.** A pale symmetric blob with legs is not a sheep, it
 * is a topiary, and no amount of fleece detail rescues it. Three properties do:
 *
 *   1. **The fleece is a SLAB, not an oval.** Its topline and its belly line are
 *      both close to flat and close to parallel (see `FLEECE`), so the body reads
 *      as a deep rectangle carried high. Otto's topline is a peak and a valley;
 *      hers is deliberately level, and the two animals are opposite in exactly
 *      that way. Round her off and she becomes the topiary.
 *   2. **The neck is BARE, long, and vertical.** It leaves the *front* of the
 *      slab and goes UP, and it carries no fleece at all. That bare vertical is
 *      what separates the small dark head from the pale mass. A woolly neck welds
 *      the two into one shape and you are back to the cloud. This is also where
 *      the column comes from: legs up, slab across, neck up, head. Three vertical
 *      elements and one horizontal, which is a column with a bar through it.
 *   3. **The ears go OUT, not up.** They leave the poll sideways and slightly
 *      back and down, and at their tips the head is 0.32 m across against a
 *      0.124 m skull — **two and a half times its own width.** That is her
 *      front-view silhouette break and it is the same job Otto's tusks do: a
 *      feature that does not cross the outline is invisible at 64 px however
 *      carefully it is modelled.
 *
 *      Note what the ears are NOT: they are not her widest point. The fleece is,
 *      at 0.43 m. The ears widen the *head*, which is the small dark shape at the
 *      top of the column, and that is the only place on her where 0.1 m of extra
 *      width changes what the shape is.
 *
 * ## She is torn, and the tearing is the story told once
 *
 * Spec §3: *wool like torn cotton wadding*. So the fleece is not fluffy-uniform —
 * it is displaced into irregular hanks that break the belly line, and there is
 * **one large patch on her left flank where the wool has been pulled out** and
 * the matted backing shows through. She has been in wire before. That is said
 * once, in a place the player sees every time she walks past, and it is never
 * said in text.
 *
 * The patch is a crater in the fleece's own parametric surface with the backing
 * laid on the crater floor — the technique Otto's repairs established, and for
 * the same reason: something generated *on* the surface can never float off it,
 * and a dark shape hovering near an animal reads as a broken mesh rather than as
 * a wound.
 *
 * ## The bell, and why it hangs where it does
 *
 * G4 — the last blocking gate in the project — is the bell. Its *sound* is a
 * separate piece of work under `docs/audio-direction.md`; what this file owes it
 * is a bell that is **on the outline**. It hangs from the collar at the front of
 * the bare neck, in the open air above her chest, so it breaks the neck's
 * silhouette against the background rather than sitting against pale fleece where
 * it would disappear. At 64 px it does not read as a bell — it reads as a lump on
 * the front of her throat, which is the correct amount of information, and is the
 * same bargain Otto's nostrils make.
 *
 * ## Scale, and the puzzle that depends on it
 *
 * She is 1.238 m to the crown against Rosalie's 0.32 m — **3.9× her height**, which
 * is more than Otto's 3.3×, because Otto is big in *mass* and Holly is big in
 * *reach*. Beat 9's door bar is at 1.4 m and is the thing only Holly can lift: her
 * muzzle stands at 1.17 m and a stretch pose takes it past 1.4, while Rosalie's
 * nudge tops out around 0.5 m. **Those two numbers are the puzzle** (`nudge`
 * cannot reach; the stretch can), so a proportion change here is a change to
 * whether beat 9 is solvable. `HOLLY_SCALE` is measured from the built mesh and
 * `build()` throws if it drifts (D-074), which is what stops that happening
 * silently.
 *
 * ## Draw calls
 *
 * Her four legs are four separate meshes rather than one merged one, which is the
 * opposite of Otto's call and is deliberate: Otto is a set-piece who lies down
 * once, and Holly *follows the player through two acts*. Legs that cannot swing
 * independently would make her walk cost more later than the four draw calls cost
 * now.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { BELL, HOLLY } from '@/render/palette';
import { surface } from './materials';
import type { AssetDefinition } from './types';

/**
 * Her standing dimensions in metres, **measured** from the built mesh (D-074).
 *
 * `height` is the top of the skull, which is the highest point on her — the
 * fleece tops out at 1.02 and the ears hang below the poll, so the head really is
 * the top of the silhouette and there is 0.26 m of bare neck under it. That gap
 * is the column, and it is the one measurement here that is load-bearing for the
 * look rather than for placement.
 */
export const HOLLY_SCALE = { length: 1.251, width: 0.43, height: 1.238 } as const;

/** Low enough to walk over the flat pinned pallet rather than route around it. */
export const HOLLY_STEP_HEIGHT = 0.18;
/** The built mesh already measures this value, so clearance cannot drift separately. */
export const HOLLY_DUCK_HEIGHT = HOLLY_SCALE.height;
/**
 * Her avoidance circle — and it is her HALF-WIDTH plus margin, not a circle that
 * would contain her.
 *
 * A circle enclosing a 1.251 m body needs 0.626 m, and an independent review
 * asked for exactly that. It is the wrong number and the reason is worth writing
 * down: at 0.626 m she cannot come within 0.63 m of anything, and the drop bar
 * she has to reach stands 0.345 m from where she must stand to reach it. The
 * enclosing circle does not make her collision better, it makes the climax of
 * beat 9 impossible — `Companion`'s constructor throws on it, which is how you
 * can check the claim rather than take it (`holly-rig.spec.ts` does).
 *
 * So it is 0.215 m of measured half-width plus margin, and what is given up is
 * real: turning broadside past a corner, her nose and rump can pass over ground
 * the circle says is free. **That was looked at, not assumed** — led into the
 * bale stack she settles at (2.33, 2.31), 26 mm off the corner, and
 * `docs/verification/beat-9/4-at-the-bales.png` is a sheep standing at a stack
 * of wool. An accepted limit for a companion who is led rather than driven, and
 * `docs/backlog.md` has the capsule version against the beat that first needs it.
 */
export const HOLLY_BODY_RADIUS = 0.30;

/** How far the declared scale may miss the measured bounds before it is a bug. */
const SCALE_TOLERANCE = 0.03;

/**
 * Her muzzle's height at rest, in metres. **Measured, and it was not before.**
 *
 * This said 1.09 for two sessions, next to a `STRETCH_REACH` of 0.34 and an
 * assertion in `build()` that `MUZZLE_Y + STRETCH_REACH > 1.4` — so beat 9's
 * drop bar was provably reachable, and the proof was two hand-written numbers
 * agreeing with each other about a mesh neither of them had looked at.
 *
 * The mesh puts it at 0.995. Her head carries 0.38 rad of nose-down on the poll
 * (see the note on `head` in `build()`), and 95 mm of the declared height was
 * that rotation, unaccounted. The real total with the old 0.34 was 1.335 against
 * a 1.4 m bar: the assertion was green and the door had no solution, which is
 * the exact failure it was written to catch. `docs/traps.md` already had the
 * entry — *the expected value and the defect had the same author*.
 *
 * Two things changed so it cannot recur:
 *
 *  - `assertScale` now measures the `muzzle` node in world space and compares it
 *    to this constant, so the number and the geometry cannot drift apart again.
 *  - **`STRETCH_REACH` is gone.** How high she can reach is a fact about the
 *    RIG, not about this file: `hollyReachClip` rears her, and what that clip
 *    achieves is measured against the actual bar in `tests/e2e/holly-rig.spec.ts`.
 *    A constant here could only ever have been a second author's guess.
 */
export const MUZZLE_Y = 0.995;

/** How far the measured muzzle may miss `MUZZLE_Y` before it is a bug. */
const MUZZLE_TOLERANCE = 0.01;

/**
 * Where the neck leaves the slab, in body coordinates. The rig turns her here.
 *
 * It starts *inside* the fleece (the chest top is 0.968) rather than on top of
 * it, so the bare neck grows out of her instead of being balanced on her. The
 * pass before this one started it at the surface and the join was visible from
 * every angle as a seam between two objects.
 */
const NECK_BASE = { y: 0.900, z: 0.262 } as const;

/**
 * `settle`'s construction correction in Z. See the long note where it is used.
 *
 * A constant rather than a literal because `HOLLY_REAR_PIVOT` needs the same
 * number, and a second copy of a measured offset is the shape of defect this
 * whole file keeps recording — two derivations that agree until one is edited.
 */
const SETTLE_Z = -0.0234;

/**
 * Her hoof claws, relative to the foot joint they hang under.
 *
 * Extracted because `HOLLY_REAR_PIVOT` has to turn her about the point she
 * actually stands on, and that is the bottom of these boxes — not the foot
 * JOINT, which is 40 mm up the leg and 12 mm behind them. Rearing about the
 * joint lifted her hind hooves 47 mm off the floor, which the gate caught and a
 * frame would have shown as her hovering.
 */
const CLAW = { halfSide: 0.013, height: 0.030, centreY: 0.015, forward: 0.012 } as const;
/**
 * Where the head sits on top of the neck. The rig raises and lowers her here.
 *
 * **0.31 m of neck at roughly 45°, carried FORWARD of the chest rather than up
 * over it.** Two turnarounds were spent finding this and both failures are worth
 * recording, because they are opposite:
 *
 *   - Poll at 1.19 directly above the shoulder: the neck was inside the fleece
 *     and the head rested straight on her back. No column at all.
 *   - Poll at 1.345 and vertical: a beautiful column, and **an alpaca**. At 64 px
 *     she read as a llama, which is a different animal with a different meaning.
 *
 * The resolution is that the column is not the neck — **the column is the whole
 * animal**, 1.2 m of her against Rosalie's 0.32 m. The neck's job is only to hold
 * the small dark head clear of the pale mass, and a short thick one leaning
 * forward into the gap in front of her chest does that while still being a
 * sheep's neck. Lengthen it and she stops being one.
 */
const POLL = { y: 1.160, z: 0.442 } as const;

/**
 * The fleece, as sampled sections, and the flat columns are the whole asset.
 *
 * Read `top` downward: 0.985, 1.012, 1.020, 1.022, 1.014, 1.000, 0.968. Read
 * `bottom` downward: 0.655, 0.600, 0.585, 0.578, 0.586, 0.600, 0.640. Across the
 * middle five sections the topline moves 8 mm and the belly line moves 8 mm —
 * **the body is a rectangle**, and it barely rounds at the ends at all.
 *
 * The first pass of this table rounded much harder (halfWidth 0.100 at the rump
 * against 0.190 at mid-body) and the turnaround came back an **egg**: the flat
 * columns were there and the ends threw them away. A slab that rounds off at both
 * ends is an oval no matter what the middle does. It matters more than it sounds
 * like it should — see the topiary note at the top of the file.
 *
 * This is the deliberate inverse of `otto.ts`'s `TORSO`, whose whole point is a
 * crest and a valley. Two animals, two toplines, one contrast.
 */
const FLEECE = [
  { z: -0.505, top: 0.985, bottom: 0.655, halfWidth: 0.148 },
  { z: -0.400, top: 1.012, bottom: 0.600, halfWidth: 0.178 },
  { z: -0.240, top: 1.020, bottom: 0.585, halfWidth: 0.188 },
  { z: -0.020, top: 1.022, bottom: 0.578, halfWidth: 0.190 },
  { z: 0.160, top: 1.014, bottom: 0.586, halfWidth: 0.184 },
  { z: 0.290, top: 1.000, bottom: 0.600, halfWidth: 0.168 },
  { z: 0.370, top: 0.968, bottom: 0.640, halfWidth: 0.130 },
] as const;

const FLEECE_BACK = FLEECE[0]?.z ?? 0;
const FLEECE_FRONT = FLEECE[FLEECE.length - 1]?.z ?? 0;

/**
 * The skull, in coordinates local to the poll, and it descends the whole way.
 *
 * A sheep's profile is **Roman** — convex over the nasal bone — so `cy` does not
 * fall in a straight line: it drops, flattens across the bridge, then drops again
 * to the nose. A straight fall gives a wedge, and a wedge at this scale reads as
 * a goat or a deer. The bulge is the species.
 */
const SKULL = [
  { z: 0.000, cy: 0.000, halfWidth: 0.064, halfHeight: 0.072 },
  { z: 0.042, cy: -0.020, halfWidth: 0.058, halfHeight: 0.062 },
  { z: 0.080, cy: -0.043, halfWidth: 0.049, halfHeight: 0.050 },
  // The bridge. `cy` moves 9 mm across 38 mm of length while the section keeps
  // shrinking, where either side of it the fall is more than twice that rate:
  // that flattening is the Roman nose, and it is the one place in the head doing
  // something other than tapering.
  { z: 0.118, cy: -0.052, halfWidth: 0.043, halfHeight: 0.044 },
  { z: 0.152, cy: -0.073, halfWidth: 0.038, halfHeight: 0.038 },
  { z: 0.178, cy: -0.098, halfWidth: 0.033, halfHeight: 0.033 },
] as const;

const SKULL_FRONT = SKULL[SKULL.length - 1]?.z ?? 0;

/** One sampled ring of the skull, widened from the `as const` table's literals. */
interface Slice { readonly z: number; readonly cy: number; readonly halfWidth: number; readonly halfHeight: number }

/**
 * The ear, and every number in it exists to widen her front view.
 *
 * `outward` widens the front view; `back` is what makes the ear visible in the
 * SIDE view, where it has to clear the back of the skull to register at all. The
 * first pass set `back` to −0.052 and the side silhouette had no ears in it —
 * from side on she was a head-shaped blob, which is most of why she read as a
 * llama. Both numbers earn their place, and they earn it in different views.
 */
const EAR = { outward: 0.150, back: -0.072, drop: -0.055, length: 0.148, halfWidth: 0.044 } as const;

/** Where the collar sits on the bare neck, and where the bell hangs from it. */
/**
 * The collar, and `radius` is the number that was wrong first time.
 *
 * The neck is about 0.080 m in radius where the collar sits, and this went wrong
 * in both directions before it was right:
 *
 *   - 0.062 is a strap *inside* her throat, and the turnaround showed exactly
 *     that — a dark ring passing through the neck rather than round it.
 *   - 0.092 clears the neck and then some, and reads as a **hoop**: a horse
 *     collar hung on a sheep, floating with daylight all round it.
 *
 * A worn strap sits ON the animal. 0.083 with a thinner tube touches the pile
 * most of the way round, which is what makes it read as buckled rather than
 * placed.
 */
const COLLAR = { y: 1.028, z: 0.334, radius: 0.083, tube: 0.010 } as const;
/**
 * The bell hangs at z = 0.45, and the fleece stops at z = 0.37.
 *
 * That 80 mm is the whole point of the position: it puts the bell in open air in
 * front of her chest, silhouetted against the background rather than against pale
 * wool. A bell at the neck's own z would be the correct place to hang one and
 * would be invisible in every frame of the game.
 */
const BELL_AT = { x: 0.010, y: 0.948, z: 0.450, radius: 0.040 } as const;

/** World Y of the bell on a standing Holly; her root is y = 0, so local and world Y agree. */
export const HOLLY_BELL_HEIGHT = BELL_AT.y;

const SEG = { fleeceRings: 26, fleeceRadial: 24, skullRings: 10, skullRadial: 12, limb: 7 } as const;

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * Deterministic value noise on the fleece's own (u, theta) parameters.
 *
 * Asset factories are pure (`docs/asset-conventions.md`) — an unseeded
 * `Math.random()` here would make every screenshot of her a different sheep and
 * the acceptance gate meaningless. This is a hash, not a generator: the same
 * point on the surface always lifts by the same amount, so the tufts are a
 * property of Holly rather than of when she was built.
 */
function hash2(a: number, b: number): number {
  const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return s - Math.floor(s);
}

/** Smoothly interpolated `hash2`, so the pile lumps rather than sparkles. */
function noise2(a: number, b: number): number {
  const ai = Math.floor(a);
  const bi = Math.floor(b);
  const af = a - ai;
  const bf = b - bi;
  const ax = af * af * (3 - 2 * af);
  const bx = bf * bf * (3 - 2 * bf);
  const n00 = hash2(ai, bi);
  const n10 = hash2(ai + 1, bi);
  const n01 = hash2(ai, bi + 1);
  const n11 = hash2(ai + 1, bi + 1);
  return (n00 * (1 - ax) + n10 * ax) * (1 - bx) + (n01 * (1 - ax) + n11 * ax) * bx;
}

interface Section {
  top: number;
  bottom: number;
  halfWidth: number;
}

function fleeceSection(z: number): Section {
  const first = FLEECE[0];
  if (first === undefined) throw new Error('holly: FLEECE is empty');
  if (z <= first.z) return { top: first.top, bottom: first.bottom, halfWidth: first.halfWidth };
  for (let i = 1; i < FLEECE.length; i += 1) {
    const a = FLEECE[i - 1];
    const b = FLEECE[i];
    if (a === undefined || b === undefined) continue;
    if (z <= b.z) {
      const t = smoothstep(a.z, b.z, z);
      return {
        top: a.top + (b.top - a.top) * t,
        bottom: a.bottom + (b.bottom - a.bottom) * t,
        halfWidth: a.halfWidth + (b.halfWidth - a.halfWidth) * t,
      };
    }
  }
  const last = FLEECE[FLEECE.length - 1];
  if (last === undefined) throw new Error('holly: FLEECE is empty');
  return { top: last.top, bottom: last.bottom, halfWidth: last.halfWidth };
}

/**
 * The torn patch on her left flank, as a 0..1 field over the fleece surface.
 *
 * `-X` is her left: with +Z forward and +Y up in a right-handed frame, her right
 * is `up × forward = +X`. Getting that backwards would put the wound on the side
 * the camera spends beat 9 looking at from the wrong angle, so it is written down
 * rather than assumed.
 */
function tornPatch(u: number, theta: number): number {
  // theta = 0 is the top of the section and increases toward +X, so 1.5π is the
  // middle of her left flank.
  const dTheta = Math.atan2(Math.sin(theta - Math.PI * 1.46), Math.cos(theta - Math.PI * 1.46));
  const du = u - 0.44;
  const r = Math.hypot(du / 0.150, dTheta / 0.60);
  return 1 - smoothstep(0.55, 1, r);
}

/**
 * How far the pile stands off the base slab at one point, in metres.
 *
 * Three terms, and each is doing a different job:
 *   - a broad lumpy term, which is the wadding;
 *   - a **belly-weighted** term, because a fleece hangs and its hanks break the
 *     underline — a ragged bottom edge is a stronger silhouette than a smooth
 *     one, and this is the only place in the animal where that is free;
 *   - the torn patch, which is NEGATIVE. The wool is gone there, so the surface
 *     dips into a crater and `buildTornGeometry` lays the backing on its floor.
 */
function pileOffset(u: number, theta: number): number {
  // **Sampled on the circle, not on theta.** Sampling `noise2(u, theta)` directly
  // is not periodic — the value at theta = 0 and at theta = 2π are different
  // numbers, so the lattice's seam does not close and the first turnaround of
  // this asset had a crease running the length of her spine. Feeding cos/sin in
  // makes the field a property of the *point*, which closes by construction.
  const cx = Math.cos(theta);
  const sx = Math.sin(theta);
  const lump = noise2(u * 7.5 + cx * 2.4, 4 + sx * 2.4);
  const hank = noise2(u * 13 + cx * 4.1, 9 + sx * 4.1);
  const underside = smoothstep(0.35, 0.95, Math.abs(Math.atan2(Math.sin(theta), Math.cos(theta))) / Math.PI);
  // The ends of the slab get almost no pile, so the rounding at rump and chest
  // survives instead of being buried under 30 mm of noise.
  const ends = smoothstep(0, 0.09, u) * (1 - smoothstep(0.9, 1, u));
  const base = (0.011 + 0.019 * lump + 0.026 * hank * underside) * ends;
  // −42 mm, against a pile that stands off by about 20 mm on the flank, and it
  // has now been wrong in BOTH directions.
  //
  // The first pass used −52 mm and the crater floor came out BELOW the backing
  // mesh that is supposed to show through it, so the wound was modelled, shaded,
  // costed — and completely invisible. Session 41 took it to −75 mm, which fixed
  // that and produced the opposite failure: at 75 mm the crater's walls are
  // steep enough to sit in the bottom band of `TOON_RAMP` from every angle, and
  // in beat 9's climax frame her flank had a **black hole punched through it**.
  // This renderer has three soft bands and no rim term (`docs/traps.md`), so a
  // deep concavity is not shaded, it is unlit.
  //
  // 42 mm is the window: still deeper than the −16 to −30 mm inset in
  // `buildTornGeometry`, so the backing stays suspended above the floor and
  // visible, and shallow enough that the walls keep a lit face. It reads as wool
  // torn away down to the skin, which is the story, rather than as a wound
  // through her, which is a different and much louder one.
  return base - 0.042 * tornPatch(u, theta);
}

/**
 * A parametric grid, closed in theta, with a real uv.
 *
 * D-073: hand-built geometry has no uv, `mergeGeometries` demands one, and the
 * failure lands on the *room*, which then refuses to boot. Every hand-built
 * surface in this file carries one for that reason.
 */
function lattice(
  rings: number,
  radial: number,
  point: (u: number, theta: number) => THREE.Vector3,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i <= rings; i += 1) {
    const u = i / rings;
    for (let j = 0; j <= radial; j += 1) {
      const v = j / radial;
      const p = point(u, v * Math.PI * 2);
      positions.push(p.x, p.y, p.z);
      uvs.push(v, u);
    }
  }
  for (let i = 0; i < rings; i += 1) {
    for (let j = 0; j < radial; j += 1) {
      const a = i * (radial + 1) + j;
      const b = a + radial + 1;
      indices.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/** Close one end of a lattice with a fan to a centre vertex. */
function fanCap(geo: THREE.BufferGeometry, radial: number, ring: number, centre: THREE.Vector3): void {
  const pos = geo.getAttribute('position');
  const uv = geo.getAttribute('uv');
  const index = geo.getIndex();
  if (!index) return;
  const positions = Array.from(pos.array);
  const uvs = Array.from(uv.array);
  const indices = Array.from(index.array);
  const centreIndex = positions.length / 3;
  positions.push(centre.x, centre.y, centre.z);
  uvs.push(0.5, ring === 0 ? 0 : 1);
  const base = ring * (radial + 1);
  for (let j = 0; j < radial; j += 1) {
    if (ring === 0) indices.push(centreIndex, base + j, base + j + 1);
    else indices.push(centreIndex, base + j + 1, base + j);
  }
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
}

/**
 * A point on the fleece.
 *
 * The cross-section is a **superellipse**, not an ellipse: `shaped()` raises the
 * sine and cosine toward a squarer profile, which is what makes the body a slab
 * with soft corners instead of a barrel. It is the single most important line in
 * the file after the `FLEECE` table — an ordinary ellipse here undoes property 1
 * at the top of this module no matter what the table says.
 */
function fleecePoint(u: number, theta: number, offset = 0): THREE.Vector3 {
  const z = FLEECE_BACK + (FLEECE_FRONT - FLEECE_BACK) * u;
  const section = fleeceSection(z);
  const centreY = (section.top + section.bottom) / 2;
  const halfHeight = (section.top - section.bottom) / 2;
  const shaped = (value: number): number => Math.sign(value) * Math.pow(Math.abs(value), 0.55);
  const sx = shaped(Math.sin(theta));
  const cy = shaped(Math.cos(theta));
  const push = offset + pileOffset(u, theta);
  return new THREE.Vector3(
    (section.halfWidth + push) * sx,
    centreY + (halfHeight + push) * cy,
    z,
  );
}

function buildFleeceGeometry(): THREE.BufferGeometry {
  const geo = lattice(SEG.fleeceRings, SEG.fleeceRadial, (u, theta) => fleecePoint(u, theta));
  const back = fleeceSection(FLEECE_BACK);
  const front = fleeceSection(FLEECE_FRONT);
  fanCap(geo, SEG.fleeceRadial, 0, new THREE.Vector3(0, (back.top + back.bottom) / 2, FLEECE_BACK - 0.03));
  // The chest cap is buried under the neck at rest, but the neck is a rig pivot
  // and turns; an open tube would show a hole through her the moment it does.
  fanCap(geo, SEG.fleeceRadial, SEG.fleeceRings, new THREE.Vector3(0, (front.top + front.bottom) / 2, FLEECE_FRONT + 0.02));
  return geo;
}

/**
 * The matted backing, on the floor of the crater the pile leaves in her flank.
 *
 * Built from `fleecePoint` at a fixed inset, so it is a patch **of** the fleece
 * surface and cannot separate from it under any later proportion change. Only the
 * part of the patch that is actually torn is emitted; the rim is pulled inward so
 * the backing tucks under the surrounding pile rather than ending in a visible
 * paper edge.
 */
function buildTornGeometry(): THREE.BufferGeometry {
  const rings = 14;
  const radial = 16;
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const centreU = 0.44;
  const centreTheta = Math.PI * 1.46;
  for (let i = 0; i <= rings; i += 1) {
    const r = i / rings;
    for (let j = 0; j <= radial; j += 1) {
      const a = (j / radial) * Math.PI * 2;
      // A ragged rim, from the same hash the pile uses, so the hole looks pulled
      // rather than cut — and pulled harder than the first pass, which came back
      // an almost perfect oval and read as a saddle patch rather than a wound.
      const ragged = 0.72 + 0.30 * noise2(Math.cos(a) * 2.4 + 5, Math.sin(a) * 2.4 + 5)
        + 0.22 * noise2(Math.cos(a) * 6.1 + 17, Math.sin(a) * 6.1 + 17);
      const u = centreU + Math.cos(a) * 0.146 * r * ragged;
      const theta = centreTheta + Math.sin(a) * 0.58 * r * ragged;
      // Shallow on purpose: the CRATER goes to −75 mm and the backing sits at
      // −16 to −30, so the matted layer is suspended well above the floor of the
      // hollow and catches the light that gets into it. Sink it to the bottom of
      // the crater — which is what the first pass did — and the wound is modelled
      // and invisible.
      const inset = -0.016 - 0.014 * (1 - r);
      const p = fleecePoint(Math.min(1, Math.max(0, u)), theta, inset - pileOffset(Math.min(1, Math.max(0, u)), theta));
      positions.push(p.x, p.y, p.z);
      uvs.push(0.5 + Math.cos(a) * r * 0.5, 0.5 + Math.sin(a) * r * 0.5);
    }
  }
  for (let i = 0; i < rings; i += 1) {
    for (let j = 0; j < radial; j += 1) {
      const a = i * (radial + 1) + j;
      const b = a + radial + 1;
      indices.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/**
 * The bare neck, from the slab to the poll — and it is the column.
 *
 * It is built in body coordinates rather than under the head pivot because the
 * rig turns the head at `POLL` and bends the neck at `NECK_BASE`; a neck welded
 * to either one would tear at the other. Two pivots, one geometry between them,
 * is the seam `RosalieRig` established.
 */
function buildNeckGeometry(): THREE.BufferGeometry {
  const rings = 12;
  const radial = 10;
  return lattice(rings, radial, (u, theta) => {
    // Slightly S-shaped: it leaves the chest angled forward, then straightens.
    // A perfectly straight neck reads as a pipe and takes the calm out of her.
    const lean = smoothstep(0, 1, u);
    const x = Math.sin(theta);
    const y = Math.cos(theta);
    // 0.085 down to 0.062, where the first pass ran 0.062 down to 0.042.
    //
    // The turnaround of that pass showed the failure this fixes: a neck that thin
    // reads as **wire**, and a head on a wire reads as a head that has come off.
    // The bare neck is supposed to be one of the three shapes she is made of (see
    // property 2 at the top of the file) — a shape, not a join.
    const r = 0.092 - 0.024 * lean;
    const cx = 0;
    const cy = NECK_BASE.y + (POLL.y - NECK_BASE.y) * lean;
    const cz = NECK_BASE.z + (POLL.z - NECK_BASE.z) * (lean * 0.72 + smoothstep(0.4, 1, u) * 0.28);
    // The section is an ellipse in the horizontal plane and the neck is nearly
    // vertical, so `y` (the section's own up) has to become DEPTH here rather
    // than height, or the neck comes out as a flat ribbon seen from the side.
    return new THREE.Vector3(cx + x * r, cy + y * r * 0.34, cz + y * r * 0.94);
  });
}

function buildSkullGeometry(): THREE.BufferGeometry {
  const first = SKULL[0];
  const last = SKULL[SKULL.length - 1];
  if (first === undefined || last === undefined) throw new Error('holly: SKULL is empty');
  const geo = lattice(SEG.skullRings, SEG.skullRadial, (u, theta) => {
    const z = first.z + (SKULL_FRONT - first.z) * u;
    let a: Slice = first;
    let b: Slice = last;
    for (let i = 1; i < SKULL.length; i += 1) {
      const lo = SKULL[i - 1];
      const hi = SKULL[i];
      if (lo === undefined || hi === undefined) continue;
      if (z <= hi.z) { a = lo; b = hi; break; }
    }
    const t = b.z === a.z ? 0 : smoothstep(a.z, b.z, z);
    const cy = a.cy + (b.cy - a.cy) * t;
    const halfWidth = a.halfWidth + (b.halfWidth - a.halfWidth) * t;
    const halfHeight = a.halfHeight + (b.halfHeight - a.halfHeight) * t;
    return new THREE.Vector3(
      Math.sin(theta) * halfWidth,
      cy + Math.cos(theta) * halfHeight,
      z,
    );
  });
  fanCap(geo, SEG.skullRadial, 0, new THREE.Vector3(0, first.cy, first.z - 0.03));
  fanCap(geo, SEG.skullRadial, SEG.skullRings, new THREE.Vector3(0, last.cy - 0.006, SKULL_FRONT + 0.016));
  return geo;
}

/**
 * One ear, in coordinates local to the poll.
 *
 * A flattened, slightly cupped blade. It is emitted as a lattice rather than a
 * scaled sphere because the cup is what stops it reading as a leaf — and because
 * the tip has to stay thin: a rounded tip at 64 px turns the ear into a mitten
 * and takes the width back off her.
 */
function buildEar(side: 1 | -1): THREE.BufferGeometry {
  const rings = 9;
  const radial = 8;
  // ── Built on an explicit frame, and the third attempt is the one that worked ─
  //
  // Attempt one mixed out/back/down into one coordinate expression and produced a
  // crumpled fin folded through the skull. Attempt two built a clean blade along
  // +X and mirrored the left one with `scale(-1, 1, 1)` — which inverts the
  // winding, so the left ear rendered inside out and the front turnaround came
  // back with **one ear**. Mirroring geometry is never the cheap trick it looks
  // like.
  //
  // So: an axis, a width direction and a thickness direction, all derived from
  // `side` rather than applied to it. Nothing is mirrored, both ears are built
  // the same way, and the front view gets the width it is supposed to get.
  const axis = new THREE.Vector3(side * EAR.outward, EAR.drop, EAR.back).normalize();
  const width = new THREE.Vector3(0, 1, 0).cross(axis).normalize();
  const thick = axis.clone().cross(width).normalize();
  const root = new THREE.Vector3(side * 0.046, -0.002, 0.010);
  const blade = lattice(rings, radial, (u, theta) => {
    const taper = 1 - smoothstep(0.28, 1, u) * 0.90;
    const halfWidth = EAR.halfWidth * taper;
    const thickness = 0.010 * taper + 0.0025;
    // The cup: the blade curls along its length, so one face catches light and
    // the other goes dark instead of it reading as a flat card.
    const cup = Math.sin(u * Math.PI) * 0.015;
    return root.clone()
      .addScaledVector(axis, EAR.length * u)
      .addScaledVector(width, Math.cos(theta) * halfWidth)
      .addScaledVector(thick, Math.sin(theta) * thickness + cup * (1 - Math.cos(theta)) * 0.5);
  });
  fanCap(blade, radial, rings, root.clone().addScaledVector(axis, EAR.length + 0.007));
  return blade;
}

/**
 * One leg: a tapered shaft down to a dark cloven hoof, in one geometry.
 *
 * The hoof shares the leg's material on purpose. A sheep's hoof and its lower leg
 * are both dark, and splitting them would double her leg draw calls for a
 * distinction that does not exist at any distance she is ever seen from.
 */
function buildLeg(joints: readonly { y: number; z: number; r: number }[]): THREE.BufferGeometry {
  const pieces: THREE.BufferGeometry[] = [];

  const shaft = lattice(joints.length * 3, SEG.limb, (u, theta) => {
    const f = u * (joints.length - 1);
    const i = Math.min(joints.length - 2, Math.floor(f));
    const a = joints[i];
    const b = joints[i + 1];
    if (a === undefined || b === undefined) return new THREE.Vector3();
    const t = f - i;
    const y = a.y + (b.y - a.y) * t;
    const z = a.z + (b.z - a.z) * t;
    const r = a.r + (b.r - a.r) * t;
    return new THREE.Vector3(Math.sin(theta) * r, y, z + Math.cos(theta) * r);
  });
  pieces.push(shaft);
  // No hoof here. The hooves are one merged mesh in `build()` because they are a
  // different material and nothing animates a hoof apart from its leg — building
  // them in both places is exactly what the first pass did, and the turnaround
  // showed two sets of claws at every foot.
  const merged = mergeGeometries(pieces, false);
  if (!merged) throw new Error('holly: leg merge failed');
  return merged;
}

/** The frayed collar, plus the threads that have come away from it. */
function buildCollarGeometry(): THREE.BufferGeometry {
  const pieces: THREE.BufferGeometry[] = [];
  const strap = new THREE.TorusGeometry(COLLAR.radius, COLLAR.tube, 6, 20);
  // −0.965 rad, which is the angle that puts the strap's plane PERPENDICULAR to
  // the neck. The neck rises 0.260 and runs forward 0.180, so its axis is
  // atan(0.180 / 0.260) = 0.606 rad off vertical, and a torus starts with its
  // normal along +Z. Getting this from the neck's own numbers rather than by eye
  // is why it does not have to be re-tuned every time the neck moves.
  strap.rotateX(-0.965);
  // Crooked, and it is the same idea as Otto's mismatched ear: a collar squared
  // up to her neck reads as equipment, a collar that has slipped reads as
  // something nobody has adjusted for a long time.
  strap.rotateZ(0.11);
  strap.translate(0, COLLAR.y, COLLAR.z);
  pieces.push(strap);
  for (let i = 0; i < 5; i += 1) {
    const a = 2.0 + i * 0.42;
    const thread = new THREE.BoxGeometry(0.004, 0.030 + 0.016 * hash2(i, 3), 0.004);
    thread.translate(
      Math.cos(a) * COLLAR.radius * 0.96,
      COLLAR.y - 0.016,
      COLLAR.z + Math.sin(a) * COLLAR.radius * 0.58,
    );
    pieces.push(thread);
  }
  const merged = mergeGeometries(pieces, false);
  if (!merged) throw new Error('holly: collar merge failed');
  return merged;
}

/**
 * The bell. Small, brass, and hung where it breaks the outline.
 *
 * A squashed sphere with a slot cut across the bottom by a second, darker piece
 * would be more accurate and would be invisible at every distance she is played
 * at, so it is a squashed sphere and a loop. What matters is that it hangs clear
 * of the fleece — see the note at the top of the file.
 */
function buildBellGeometry(): THREE.BufferGeometry {
  const pieces: THREE.BufferGeometry[] = [];
  const body = new THREE.SphereGeometry(BELL_AT.radius, 10, 8);
  body.scale(1, 0.92, 0.94);
  body.translate(BELL_AT.x, BELL_AT.y, BELL_AT.z);
  pieces.push(body);
  const loop = new THREE.TorusGeometry(0.014, 0.005, 5, 10);
  loop.rotateY(Math.PI / 2);
  loop.translate(BELL_AT.x, BELL_AT.y + BELL_AT.radius * 0.92, BELL_AT.z);
  pieces.push(loop);
  const merged = mergeGeometries(pieces, false);
  if (!merged) throw new Error('holly: bell merge failed');
  return merged;
}

function mesh(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const result = new THREE.Mesh(geometry, material);
  result.name = name;
  result.castShadow = true;
  result.receiveShadow = true;
  return result;
}

function pivot(name: string, x: number, y: number, z: number): THREE.Object3D {
  const result = new THREE.Object3D();
  result.name = name;
  result.position.set(x, y, z);
  return result;
}

function merged(name: string, pieces: readonly THREE.BufferGeometry[]): THREE.BufferGeometry {
  const geometry = mergeGeometries([...pieces], false);
  if (!geometry) throw new Error(`holly: ${name} merge failed`);
  return geometry;
}

/**
 * Her legs, and they are longer than a sheep's really are.
 *
 * `docs/act-2-plan.md` asks for **lanky**, and lankiness is the ratio of leg
 * length to body depth, not absolute height: hers are 0.58 m under a 0.44 m slab,
 * where a real ewe's are shorter than her body is deep. Shorten these and she
 * gets heavy and placid-looking, which is a different animal from calm.
 */
const FRONT_LEG = [
  { y: 0.620, z: 0.190, r: 0.052 },
  { y: 0.420, z: 0.196, r: 0.036 },
  { y: 0.210, z: 0.188, r: 0.028 },
  { y: 0.040, z: 0.182, r: 0.026 },
] as const;

const HIND_LEG = [
  { y: 0.640, z: -0.322, r: 0.058 },
  { y: 0.430, z: -0.352, r: 0.040 },
  // The hock. A sheep's hind leg breaks backward here, and the kink is most of
  // what tells a hind leg from a front one in a 64 px silhouette.
  { y: 0.235, z: -0.300, r: 0.027 },
  { y: 0.040, z: -0.312, r: 0.025 },
] as const;

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'holly';

  // `breath` is the whole body; `settle` is construction correction only. Keeping
  // them separate means a later proportion change cannot quietly move the axis a
  // breathing clip animates about — the lesson `otto.ts` records for its roll.
  const breath = pivot('breath', 0, 0, 0);
  // `settle` carries −23.4 mm in Z, and it is measured, not chosen.
  //
  // `docs/asset-conventions.md` requires the origin to be CENTRED in X/Z as well
  // as at ground contact, and she is not naturally centred: her head hangs
  // forward of her chest while her rump stops at the fleece. Uncorrected, every
  // placement of her in a room would be 23 mm out, which is inside the gate's
  // 30 mm tolerance by luck rather than by design.
  //
  // **Re-measure it after any change to the skull, the neck or the ends of
  // `FLEECE`** — it has changed with all three during this asset's authoring, and
  // the asset gate reports the exact offset when it fails, so there is never a
  // reason to guess. It goes on `settle` rather than on `breath` or the group
  // because this node is construction correction only: keeping it here means a
  // later proportion change cannot quietly move the axis a clip animates about.
  const settle = pivot('settle', 0, 0, SETTLE_Z);
  group.add(breath);
  breath.add(settle);

  const fleeceMat = surface('fabric', HOLLY.fleece);
  const tornMat = surface('fabric', HOLLY.torn);
  const bareMat = surface('fabric', HOLLY.bare);
  const muzzleMat = surface('fabric', HOLLY.muzzle);
  const buttonMat = surface('fabric', HOLLY.button);
  const hoofMat = surface('fabric', HOLLY.hoof);
  const collarMat = surface('fabric', HOLLY.collar);
  const bellMat = surface('metal', BELL);

  settle.add(mesh(buildFleeceGeometry(), fleeceMat, 'fleece'));
  settle.add(mesh(buildTornGeometry(), tornMat, 'torn-flank'));

  // ── neck and head ──────────────────────────────────────────────────────────
  const neck = pivot('neck', 0, 0, 0);
  settle.add(neck);
  neck.add(mesh(buildNeckGeometry(), bareMat, 'neck'));

  const head = pivot('head', 0, POLL.y, POLL.z);
  // **Nose down 22°, on the pivot rather than in the table.**
  //
  // The first turnaround of this asset had the head sticking straight out along
  // +Z and it read as a sausage on a stick — an alpaca, not a sheep. A grazing
  // animal at rest carries its muzzle *below* its poll even when it is looking at
  // you, and that one rotation is most of the difference between watchful and
  // startled.
  //
  // It goes on the pivot because it is a POSE, and a rig raising her to the 1.4 m
  // drop bar will drive exactly this angle. Baking it into `SKULL` would make the
  // table describe a head that is looking down, which is a different and much
  // less editable thing than a head, looking down.
  head.rotation.x = 0.38;
  neck.add(head);
  head.add(mesh(buildSkullGeometry(), bareMat, 'skull'));

  const noseSample = SKULL[SKULL.length - 1];
  const noseY = noseSample?.cy ?? 0;
  // The muzzle, and it is darker than the face. A sheep's nose is the one place
  // on her that is nearly black, and it is what stops the head reading as a sock.
  const nose = new THREE.SphereGeometry(0.030, 8, 6);
  nose.scale(1.05, 0.86, 0.62);
  nose.translate(0, noseY - 0.004, SKULL_FRONT + 0.012);
  head.add(mesh(nose, muzzleMat, 'muzzle'));

  const eyes: THREE.BufferGeometry[] = [];
  for (const side of [1, -1] as const) {
    const eye = new THREE.SphereGeometry(0.019, 8, 6);
    eye.scale(1, 0.9, 0.6);
    // Set WIDE and low on the skull. Sheep eyes are on the sides of the head, and
    // moving them forward is the single fastest way to turn her into a cartoon
    // dog — which would make the two-shot two pets instead of two animals.
    eye.translate(side * 0.046, -0.012, 0.062);
    eyes.push(eye);
  }
  head.add(mesh(merged('eyes', eyes), buttonMat, 'eyes'));

  head.add(mesh(merged('ears', [buildEar(1), buildEar(-1)]), bareMat, 'ears'));

  // ── collar and bell ────────────────────────────────────────────────────────
  //
  // Both hang off the NECK pivot rather than off the body, so when the rig turns
  // her head to look at Rosalie the bell goes with her throat. A bell parented to
  // the body would stay behind and the beat's central image would break.
  neck.add(mesh(buildCollarGeometry(), collarMat, 'collar'));
  neck.add(mesh(buildBellGeometry(), bellMat, 'bell'));

  // ── legs ───────────────────────────────────────────────────────────────────
  //
  // Four meshes, four pivots. See the note on draw calls at the top of the file:
  // she walks for two acts, and a merged set of legs would have to be undone the
  // first time she took a step.
  for (const [name, joints, x] of [
    ['leg-front-right', FRONT_LEG, 0.118],
    ['leg-front-left', FRONT_LEG, -0.118],
    ['leg-hind-right', HIND_LEG, 0.132],
    ['leg-hind-left', HIND_LEG, -0.132],
  ] as const) {
    const top = joints[0];
    const hip = pivot(name, x, top.y, top.z);
    const geometry = buildLeg(joints);
    geometry.translate(0, -top.y, -top.z);
    hip.add(mesh(geometry, bareMat, `${name}-mesh`));

    // **Her hooves hang off the HIP, two per leg, and they used to hang off her
    // body.**
    //
    // All eight claws were one merged mesh parented to `settle`, under a comment
    // reading *"merged into one mesh because nothing animates a hoof separately
    // from the leg it is on"*. The reasoning is right and the conclusion inverted
    // it: one mesh on the body cannot follow four pivots, so the hooves animated
    // separately from every leg. Nothing had caught it because nothing had ever
    // animated her — the first frame of `hollyWalkClip` would have swung four
    // bare stumps out of four stationary feet.
    //
    // The cost of fixing it is three extra draw calls (four hoof meshes instead
    // of one), and it is paid because she walks for two acts. It stays a MERGE
    // per leg rather than a mesh per claw for the reason the original comment
    // gave, which was never the part that was wrong.
    const claws: THREE.BufferGeometry[] = [];
    const foot = joints[joints.length - 1];
    if (foot !== undefined) {
      for (const side of [1, -1] as const) {
        const claw = new THREE.BoxGeometry(0.019, CLAW.height, 0.052);
        // Hip-local: the same placement as before, less the pivot it now hangs on.
        claw.translate(side * CLAW.halfSide, CLAW.centreY - top.y, foot.z + CLAW.forward - top.z);
        claws.push(claw);
      }
      hip.add(mesh(merged(`${name}-hooves`, claws), hoofMat, `${name}-hooves`));
    }
    settle.add(hip);
  }

  // A short docked tail with the fleece still on it — the only thing that breaks
  // her rump's outline.
  //
  // It has to sit BEHIND the back cap (which reaches z − 0.03) and BELOW the
  // fleece's belly line, or it is inside her: the first pass put it at y 0.845,
  // squarely within a section running 0.655 to 0.985, and the rear turnaround
  // came back with no tail at all. Geometry that is inside other geometry is not
  // subtle, it is absent.
  const tail = new THREE.SphereGeometry(0.040, 8, 6);
  tail.scale(0.62, 1.35, 0.62);
  tail.rotateX(-0.55);
  tail.translate(0, 0.790, FLEECE_BACK - 0.062);
  settle.add(mesh(tail, fleeceMat, 'tail'));

  assertScale(group);
  return group;
}

/**
 * D-074: the declared footprint is what the geometry measures, not what the
 * author meant — and here it is also what makes beat 9 solvable.
 *
 * The muzzle check is the important one. Beat 9's drop bar is authored against
 * `MUZZLE_Y`, so a proportion change that lowered her head would otherwise show
 * up as "the sheep does not open the door", three sessions later, in a room
 * nobody suspected. It measures the node; the version that compared two
 * constants in this file is why the number was wrong by 95 mm.
 */
function assertScale(group: THREE.Group): void {
  const box = new THREE.Box3().setFromObject(group);
  const size = box.getSize(new THREE.Vector3());
  const checks: readonly [string, number, number][] = [
    ['length', size.z, HOLLY_SCALE.length],
    ['width', size.x, HOLLY_SCALE.width],
    ['height', size.y, HOLLY_SCALE.height],
  ];
  for (const [label, measured, declared] of checks) {
    if (Math.abs(measured - declared) > SCALE_TOLERANCE) {
      throw new Error(
        `holly ${label} measured ${measured.toFixed(3)} m but HOLLY_SCALE declares ${declared} m`,
      );
    }
  }
  if (Math.abs(box.min.y) > SCALE_TOLERANCE) {
    throw new Error(`holly origin must sit at ground contact; lowest point is ${box.min.y.toFixed(3)} m`);
  }
  // The muzzle, measured rather than asserted from a sibling constant. See the
  // note on `MUZZLE_Y`: the version of this check that compared two numbers in
  // this file passed while the mesh was 95 mm lower than either of them said.
  const at = measureMuzzle(group);
  if (Math.abs(at.y - MUZZLE_Y) > MUZZLE_TOLERANCE) {
    throw new Error(
      `holly muzzle sits at ${at.y.toFixed(3)} m but MUZZLE_Y declares ${MUZZLE_Y.toFixed(3)}; `
      + 'beat 9\'s drop bar is authored against this number',
    );
  }
}

/**
 * The muzzle's centre in world space.
 *
 * **Its bounding box, not its node position**, and the difference is 165 mm. The
 * nose is a sphere whose offset is baked into its geometry by `translate()`, so
 * the mesh's own transform is the identity and `getWorldPosition` returns the
 * HEAD PIVOT — 1.160 m, the top of her poll, which is neither her nose nor
 * wrong-looking enough to notice in a number. The first version of the reach
 * gate did exactly that and reported her nose 165 mm above where it is.
 *
 * Which is the same mistake `MUZZLE_Y` itself records, made a second time in the
 * instrument built to catch it: **measure the geometry, not the node it hangs
 * on.**
 */
export function measureMuzzle(root: THREE.Object3D): THREE.Vector3 {
  const muzzle = root.getObjectByName('muzzle');
  if (!muzzle) throw new Error('holly: muzzle node missing, so its reach cannot be measured');
  root.updateMatrixWorld(true);
  return new THREE.Box3().setFromObject(muzzle).getCenter(new THREE.Vector3());
}

/**
 * The nodes `HollyRig` drives, resolved by name.
 *
 * The same seam `findRosalieParts` is: `src/anim/` never learns how she is
 * built, and `build()` can move a pivot without a clip knowing (D-015). The
 * `muzzle` node is in here for one reason — it is what the reach gate measures,
 * and a gate that cannot reach the thing it is asserting about is the whole
 * defect `MUZZLE_Y` records above.
 */
export interface HollyParts {
  /** Whole body. Carries the breath, the bob and the rear. */
  breath: THREE.Object3D;
  neck: THREE.Object3D;
  head: THREE.Object3D;
  /** Not driven. Read by the reach gate to measure how high her nose gets. */
  muzzle: THREE.Object3D;
  legFL: THREE.Object3D;
  legFR: THREE.Object3D;
  legHL: THREE.Object3D;
  legHR: THREE.Object3D;
}

function requiredNode(root: THREE.Object3D, name: string): THREE.Object3D {
  const found = root.getObjectByName(name);
  if (!found) throw new Error(`holly rig: node "${name}" missing`);
  return found;
}

export function findHollyParts(root: THREE.Object3D): HollyParts {
  return {
    breath: requiredNode(root, 'breath'),
    neck: requiredNode(root, 'neck'),
    head: requiredNode(root, 'head'),
    muzzle: requiredNode(root, 'muzzle'),
    legFL: requiredNode(root, 'leg-front-left'),
    legFR: requiredNode(root, 'leg-front-right'),
    legHL: requiredNode(root, 'leg-hind-left'),
    legHR: requiredNode(root, 'leg-hind-right'),
  };
}

/**
 * Where she pivots when she rears, in her own space: the hind hoof contact.
 *
 * Derived from `HIND_LEG`'s last joint and `settle`'s construction offset rather
 * than written down, because both have moved during this asset's authoring and a
 * rear about the wrong point puts her back feet through the floor — which is the
 * one thing that would make the beat's climactic image unusable.
 */
export const HOLLY_REAR_PIVOT: { readonly y: number; readonly z: number } = (() => {
  const hoof = HIND_LEG[HIND_LEG.length - 1];
  if (hoof === undefined) throw new Error('holly: hind leg has no foot joint to rear about');
  // The BOTTOM of the claw, not the joint. `CLAW` says why.
  return { y: CLAW.centreY - CLAW.height / 2, z: hoof.z + CLAW.forward + SETTLE_Z };
})();

export const hollyAsset: AssetDefinition = {
  id: 'holly',
  label: 'Holly',
  description: 'The sheep. Tall, calm, off-white, torn — and she wears the bell.',
  footprint: {
    width: HOLLY_SCALE.width,
    height: HOLLY_SCALE.height,
    length: HOLLY_SCALE.length,
  },
  create: build,
};
